import { mutation, internalMutation } from "../_generated/server";
import { v } from "convex/values";
import { ConvexError } from "convex/values";

// Validation constants
const MAX_WORD_LENGTH = 50;
const MAX_PHRASE_LENGTH = 200;
const MAX_LIST_LENGTH = 100;

export const updateUserPreferences = mutation({
  args: {
    userId: v.optional(v.string()),
    restrictedWords: v.optional(v.array(v.string())),
    strikeListWords: v.optional(v.array(v.string())),
    strikeListPhrases: v.optional(v.array(v.string())),
    strikeListSimilarities: v.optional(v.array(v.string())),
    strikeListLiteraryDevices: v.optional(v.array(v.string())),
    monthlyLimit: v.optional(v.number()),
    selectedModel: v.optional(v.string()),
    monthlyUsage: v.optional(v.number()),
    serviceTier: v.union(v.literal("free"), v.literal("pro"), v.literal("enterprise"), v.literal("enterpriseVIP")),
    defaultTone: v.optional(v.string()),
    isMarketerMode: v.optional(v.boolean()),
    defaultTargetAudience: v.optional(v.string()),
    defaultContentGoal: v.optional(v.string()),
    defaultCtaType: v.optional(v.string()),
    defaultFramework: v.optional(v.string()),
    defaultWordCount: v.optional(v.number()),
    enableSectionLabeling: v.boolean(), 
    createdAt: v.optional(v.number()),
    updatedAt: v.optional(v.number()),
    subscriptionStatus: v.optional(v.union(
      v.literal("active"),
      v.literal("canceled"),
      v.literal("past_due")
    )),
  },
  handler: async (ctx, args) => {
    const identity = await ctx.auth.getUserIdentity();
    const userId = args.userId || identity?.subject;
    if (!userId) {
      throw new Error("Unauthenticated");
    }

    // Validate arrays
    if (args.restrictedWords) {
      validateStringArray(args.restrictedWords, "restrictedWords", MAX_WORD_LENGTH, MAX_LIST_LENGTH);
    }
    
    if (args.strikeListWords) {
      validateStringArray(args.strikeListWords, "strikeListWords", MAX_WORD_LENGTH, MAX_LIST_LENGTH);
    }
    
    if (args.strikeListPhrases) {
      validateStringArray(args.strikeListPhrases, "strikeListPhrases", MAX_PHRASE_LENGTH, MAX_LIST_LENGTH);
    }
    
    if (args.strikeListSimilarities) {
      validateStringArray(args.strikeListSimilarities, "strikeListSimilarities", MAX_PHRASE_LENGTH, MAX_LIST_LENGTH);
    }
    
    if (args.strikeListLiteraryDevices) {
      validateStringArray(args.strikeListLiteraryDevices, "strikeListLiteraryDevices", MAX_PHRASE_LENGTH, MAX_LIST_LENGTH);
    }
    
    // Validate numbers
    if (args.monthlyLimit !== undefined && (args.monthlyLimit < 0 || args.monthlyLimit > 10000)) {
      throw new ConvexError("Monthly limit must be between 0 and 10000");
    }
    
    if (args.monthlyUsage !== undefined && (args.monthlyUsage < 0 || args.monthlyUsage > 10000)) {
      throw new ConvexError("Monthly usage must be between 0 and 10000");
    }
    
    if (args.defaultWordCount !== undefined && (args.defaultWordCount < 100 || args.defaultWordCount > 10000)) {
      throw new ConvexError("Default word count must be between 100 and 10000");
    }

    const existing = await ctx.db
      .query("userPreferences")
      .withIndex("by_userId", (q) => q.eq("userId", userId))
      .first();

    const now = Date.now();
    const updates = {
      ...args,
      updatedAt: now
    };

    if (existing) {
      await ctx.db.patch(existing._id, updates);
    } else {
      await ctx.db.insert("userPreferences", {
        ...updates,
        userId,
        monthlyLimit: args.monthlyLimit ?? 3,
        monthlyUsage: args.monthlyUsage ?? 0,
        serviceTier: args.serviceTier ?? "free",
        enableSectionLabeling: args.enableSectionLabeling ?? true,
        createdAt: now,
      });
    }
  },
});

export const updateSubscriptionInfo = internalMutation({
  args: {
    userId: v.string(),
    subscriptionId: v.optional(v.string()),
    subscriptionStatus: v.optional(v.union(
      v.literal("active"),
      v.literal("canceled"),
      v.literal("past_due")
    )),
  },
  handler: async (ctx, args) => {
    const existing = await ctx.db
      .query("userPreferences")
      .withIndex("by_userId", (q) => q.eq("userId", args.userId))
      .first();

    if (existing) {
      await ctx.db.patch(existing._id, {
        subscriptionId: args.subscriptionId,
        subscriptionStatus: args.subscriptionStatus,
        updatedAt: Date.now(),
      });
    }
  },
});

export const deleteUserPreferences = internalMutation({
  args: {
    userId: v.string(),
  },
  handler: async (ctx, args) => {
    const prefs = await ctx.db
      .query("userPreferences")
      .withIndex("by_userId", (q) => q.eq("userId", args.userId))
      .first();

    if (prefs) {
      await ctx.db.delete(prefs._id);
    }
  },
});

// Helper function to validate string arrays
function validateStringArray(array: string[], name: string, maxItemLength: number, maxArrayLength: number) {
  // Check array length
  if (array.length > maxArrayLength) {
    throw new ConvexError(`${name} array cannot have more than ${maxArrayLength} items`);
  }
  
  // Check each item
  for (const item of array) {
    if (item.length === 0) {
      continue; // Skip empty strings
    }
    if (item.length > maxItemLength) {
      throw new ConvexError(`${name} items cannot be longer than ${maxItemLength} characters`);
    }
  }
}